<template>
  <div>
    <app-title-bar>
      <app-material-icon name="home_storage" />
      <h4 class="text-text-primary">{{ $t('subscriptions.views.SubscriptionProjects.activeRooms') }}</h4>
    </app-title-bar>

    <div class="d-flex flex-column row-gap-4 layout-spacing">
      <div class="d-flex align-center">
        <SearchTextField v-model="searchQuery"/>
        <app-button
          type="outlined"
          variant="neutral"
          class="ml-4"
          @click="openFiltersPane"
        >
          <template v-if="filtersCount === 0">
            <app-material-icon name="instant_mix" />
          </template>
          <template v-else-if="filtersCount > 0">
            <v-avatar
              color="surface-grey-bold"
              class="text-text-inverted detail-md-regular"
              size="19"
            >
              {{ filtersCount }}
            </v-avatar>
          </template>
          {{ $t('subscriptions.views.SubscriptionProjects.filters') }}
        </app-button>
        <AppDownloadFile :link="exportLink" class="ml-auto">
          <template #default="{submitForm}">
            <app-button
              type="outlined"
              variant="neutral"
              @click="submitForm"
            >
              <app-material-icon name="file_export" />
              {{ $t('subscriptions.views.SubscriptionProjects.export') }}
            </app-button>
          </template>
        </AppDownloadFile>
      </div>

      <DataTableFiltersSummary v-if="filtersCount > 0"
                               :active-filters="activeRoomsFilters"
                               class="mt-4"
                               @clear-filter="clearFilter"
                               @clear-all-filters="clearAllFilters"
      />

      <template v-if="activeRoomsPending && !activeRooms ">
        <v-row>
          <v-col class="d-flex justify-center">
            <AppDataTableSkeletonLoader class="w-100"/>
          </v-col>
        </v-row>
      </template>

      <template v-if="activeRooms && !activeRoomsPending">
        <v-data-table class="SubscriptionProjects-table"
                      :items="filteredActiveRooms"
                      :headers="headers"
                      :page="currentPage"
                      :items-per-page="itemsPerPage"
        >
          <template #item.type="{item}">
            <RoomTypeIcon class="td-mobile-view" :room="item" />
          </template>
          <template #item.cmRef="{item}">
            {{ item.cmRef || '-' }}
          </template>
          <template #item.label="{item}">
            <AppProjectLabel v-if="item.roomLabel" :label="item.roomLabel" />
          </template>
          <template #item.creator="{item}">
            <div>
              <AppTooltip>
                <template #activator="{ props }">
                  <div class="text-truncated" v-bind="props">
                    {{ item.creator }}
                  </div>
                </template>
                <span>{{ item.creator }}</span>
              </AppTooltip>

              <AppTooltip>
                <template #activator="{ props }">
                  <div class="text-truncated" v-bind="props">
                    <a :href="`mailto:${item.creatorEmail}`"
                        class="creator-email app-link detail-md-regular"
                    >
                      {{ item.creatorEmail }}
                    </a>
                  </div>
                </template>
                <span>{{ item.creatorEmail }}</span>
              </AppTooltip>
            </div>
          </template>
          <template #item.createdAt="{item}">
            <DateDisplay :value="item.createdAt"/>
          </template>
          <template #item.users="{item}">
            <template v-if="item.usersCount !== -1">{{ item.usersCount }}</template>
            <template v-else>-</template>
          </template>
          <template #item.storage="{item}">
            <app-filesize-display v-if="item.storage" :value="item.storage" />
            <div v-else>-</div>
          </template>
          <template #item.signedDocuments="{item}">
            <template v-if="item.signedDocuments !== -1">{{ item.signedDocuments }}</template>
            <template v-else>-</template>
          </template>

          <template #bottom>
            <div class="d-flex justify-end mt-4">
              <AppPagination v-model:current-page="currentPage"
                              v-model:page-size="itemsPerPage"
                              :pageSizes="ITEMS_PER_PAGE"
                              :total="filteredActiveRooms.length"
              />
            </div>
          </template>
        </v-data-table>
      </template>
    </div>
    <SubscriptionActiveProjectsFiltersPane />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapMutations, mapState, useStore } from 'vuex'

import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppProjectLabel from '@/common/AppProjectLabel.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import DataTableFiltersSummary from '@/common/filters/DataTableFiltersSummary.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import RoomTypeIcon from '@/common/RoomTypeIcon.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { toast } from '@/common/utils/toast'
import { GET_ACTIVE_ROOMS } from '@/store/modules/subscription/action_types'
import { REMOVE_ACTIVE_ROOMS_FILTER, SET_ACTIVE_ROOMS_FILTERS, SET_ACTIVE_ROOMS_FILTERS_PANE_IS_OPEN } from '@/store/modules/subscription/mutation_types'

import { SUBSCRIPTION_PROJECTS_ITEMS_PER_PAGE } from '../constants.js'
import SubscriptionActiveProjectsFiltersPane from '../SubscriptionActiveProjectsFiltersPane.vue'

const ITEMS_PER_PAGE = [50, 100, 250]

export default {
  name: 'SubscriptionProjects',
  components: {
    AppDataTableSkeletonLoader,
    AppDownloadFile,
    AppPagination,
    AppProjectLabel,
    AppTooltip,
    DataTableFiltersSummary,
    DateDisplay,
    RoomTypeIcon,
    SearchTextField,
    SubscriptionActiveProjectsFiltersPane,
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: SUBSCRIPTION_PROJECTS_ITEMS_PER_PAGE,
      pageSize: ITEMS_PER_PAGE[0],
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    return {
      headers: [
        { value: 'type', title: this.$t('subscriptions.views.SubscriptionProjects.headers.type')},
        {
          value: 'name',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.name'),
          sortable: true,
        },
        {
          value: 'cmRef',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.cmRef'),
          sortable: true,
        },
        {
          value: 'label',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.label'),
          sortable: true,
        },
        {
          value: 'creator',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.creator'),
          sortable: true,
        },
        {
          value: 'createdAt',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.creationDate'),
          sortable: true,
          align: 'center',
        },
        {
          value: 'users',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.users'),
          sortable: true,
          align: 'center',
        },
        {
          value: 'storage',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.size'),
          sortable: true,
          align: 'center',
        },
        {
          value: 'signedDocuments',
          title: this.$t('subscriptions.views.SubscriptionProjects.headers.signedDocuments'),
          sortable: true,
          align: 'center',
        },
      ],
      searchQuery: '',
      ITEMS_PER_PAGE,
    }
  },
  computed: {
    ...mapState('subscription', ['activeRooms', 'activeRoomsPending', 'activeRoomsFilters']),
    ...mapGetters('user', ['currentUserId']),
    filtersCount () {
      return this.activeRoomsFilters.length
    },
    filteredActiveRooms () {
      let filteredActiveRooms = cloneDeep(this.activeRooms.data)
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filteredActiveRooms = filteredActiveRooms.filter(activeRoom => {
          return activeRoom.name.toLowerCase().includes(query) ||
            activeRoom.cmRef?.toLowerCase().includes(query) ||
            activeRoom.creator.toLowerCase().includes(query) ||
            activeRoom.creatorEmail.toLowerCase().includes(query)
        })
      }

      const memberFilters = this.activeRoomsFilters.filter(filter => filter.category.key === 'members')
      if (memberFilters.length > 0) {
        filteredActiveRooms = filteredActiveRooms.filter(room => {
          return memberFilters.some((filterMember) => room.users.some((roomUser) => filterMember.value.key === roomUser.fullName))
        })
      }

      const labelFilters = this.activeRoomsFilters.filter(filter => filter.category.key === 'labels')
      if (labelFilters.length > 0) {
        filteredActiveRooms = filteredActiveRooms.filter(room => {
          return labelFilters.some((filterLabel) => room.label && (room.roomLabel.id === filterLabel.value.key.id))
        })
      }

      const projectTypeFilters = this.activeRoomsFilters.filter(filter => filter.category.key === 'projectType')
      if (projectTypeFilters.length > 0 && projectTypeFilters.length !== 2) {
        if (projectTypeFilters[0].value.key === 'project') {
          filteredActiveRooms = filteredActiveRooms.filter(room => { return !room.isDataroom })
        }
        if (projectTypeFilters[0].value.key === 'dataroom') {
          filteredActiveRooms = filteredActiveRooms.filter(room => { return room.isDataroom })
        }
      }

      const createdAtFilter = this.activeRoomsFilters.find(filter => filter.category.key === 'createdAt')
      if (createdAtFilter) {
        const filterFrom = createdAtFilter.value.key[0]
        const filterTo = createdAtFilter.value.key[1]

        if (filterFrom && !filterTo) {
          filteredActiveRooms = filteredActiveRooms.filter(room => {
            return dayjs(room.createdAt).isSameOrAfter(dayjs(filterFrom))
          })
        } else if (filterFrom && filterTo) {
          filteredActiveRooms = filteredActiveRooms.filter(room => {
            return dayjs(room.createdAt).isBetween(dayjs(filterFrom), dayjs(filterTo), 'day', '[]')
          })
        }
      }

      return filteredActiveRooms
    },
    exportLink () {
      return `${import.meta.env.VUE_APP_API_URL}/subscription/download/rooms-excel`
    },
  },
  created () {
    this.prepareGetActiveRooms()
  },
  methods: {
    ...mapMutations('subscription', [SET_ACTIVE_ROOMS_FILTERS_PANE_IS_OPEN, SET_ACTIVE_ROOMS_FILTERS, REMOVE_ACTIVE_ROOMS_FILTER]),
    ...mapActions('subscription', [GET_ACTIVE_ROOMS]),
    async prepareGetActiveRooms () {
      try {
        await this.GET_ACTIVE_ROOMS()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('subscriptions.views.SubscriptionProjects.getActiveRoomsError'))
      }
    },
    openFiltersPane () {
      this.SET_ACTIVE_ROOMS_FILTERS_PANE_IS_OPEN(true)
    },
    clearFilter (filter) {
      this.REMOVE_ACTIVE_ROOMS_FILTER(filter)
    },
    clearAllFilters () {
      this.SET_ACTIVE_ROOMS_FILTERS([])
      this.$emit('update:searchQuery', '')
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/typography-tokens";

:deep(.SubscriptionProjects-table) {
  background: transparent;

  table {
    border-collapse: separate;
    border-spacing: 0 8px;
  }
  th {
    color: rgb(var(--v-theme-text-primary)) !important;
    border-bottom: none !important;

    .v-data-table-header__content {
      @include text('heading-xs-bold');
    }
  }
  td {
    @include text('body-md-regular');
    color: rgb(var(--v-theme-text-secondary)) !important;
    border-bottom: none !important;
    vertical-align: center;
    height: 52px !important;

    &:first-child {
      border-top-left-radius: get-radius('border-radius-2');
      border-bottom-left-radius: get-radius('border-radius-2');
    }
    &:last-child {
      border-top-right-radius: get-radius('border-radius-2');
      border-bottom-right-radius: get-radius('border-radius-2');
    }
  }

  tbody {
    tr {
      background-color: rgb(var(--v-theme-surface-primary));
      height: 62px;
    }
  }

  .creator-email {
    color: rgb(var(--v-theme-text-link));
    text-decoration: none;

    &:hover {
      color: rgb(var(--v-theme-text-link-hover));
    }
  }
}
</style>
